import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LoginIcon from '@mui/icons-material/Login';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { companiesApi, type CompanyUsage } from '@/features/companies/companies.api';
import { CompanyTableSkeleton } from '@/components/CompanyTableSkeleton';
import { SortableTableCell } from '@/components/SortableTableCell';
import { getErrorMessage } from '@/utils/errorHandling';
import { formatCNPJ } from '@/utils/cnpj';
import { formatBytes } from '@/utils/bytes';
import { useNotification } from '@/contexts/NotificationContext';
import type { SortOrder } from '@/types/pagination';

type SortKey =
  | 'legalName'
  | 'cnpj'
  | 'trial'
  | 'customerPercentage'
  | 'aiPercentage'
  | 'storagePercentage';

function formatDate(value: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR').format(new Date(value));
}

function formatPhone(value: string | null): string {
  if (!value) return 'Não informado';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return value;
}

function trialStatus(company: CompanyUsage): {
  label: string;
  color: 'success' | 'warning' | 'default';
} {
  if (company.trialLifecycleStatus === 'cleaned') {
    return { label: 'Expirado e limpo', color: 'default' };
  }
  if (company.trialLifecycleStatus === 'expired') {
    return { label: 'Expirado', color: 'warning' };
  }
  return { label: 'Ativo', color: 'success' };
}

function progressColor(percentage: number): 'success' | 'warning' | 'error' {
  if (percentage >= 90) return 'error';
  if (percentage >= 70) return 'warning';
  return 'success';
}

interface UsageCellProps {
  used: number;
  limit: number;
  percentage: number;
  unit?: string;
  formatter?: (value: number) => string;
}

const UsageCell: React.FC<UsageCellProps> = ({ used, limit, percentage, unit, formatter }) => {
  const format = (value: number) =>
    formatter ? formatter(value) : `${value.toLocaleString('pt-BR')}${unit ? ` ${unit}` : ''}`;

  return (
    <Box sx={{ minWidth: 140 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {format(used)} / {format(limit)}
        </Typography>
        <Typography variant="caption" fontWeight={600}>
          {percentage}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(percentage, 100)}
        color={progressColor(percentage)}
      />
    </Box>
  );
};

export const TrialCompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('trial');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'trial-companies', { page, limit }],
    queryFn: () => companiesApi.listTrials({ page, limit }),
  });

  const cleanupMutation = useMutation({
    mutationFn: () => companiesApi.cleanupExpiredTrials(),
    onSuccess: result => {
      setCleanupDialogOpen(false);
      void refetch();
      const count = result.cleanedCompanyIds.length;
      showNotification(
        count === 1 ? '1 trial expirado foi limpo.' : `${count} trials expirados foram limpos.`,
        'success'
      );
    },
    onError: err => {
      showNotification(getErrorMessage(err, 'Erro ao limpar trials expirados'), 'error');
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: (companyId: string) => companiesApi.impersonate(companyId),
    onSuccess: ({ code, clientAppUrl }) => {
      const url = `${clientAppUrl.replace(/\/$/, '')}/impersonate?code=${encodeURIComponent(code)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onError: err => {
      showNotification(getErrorMessage(err, 'Erro ao impersonar empresa'), 'error');
    },
  });

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder(previous => (previous === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };

  const filteredAndSorted = useMemo<CompanyUsage[]>(() => {
    const term = search.trim().toLowerCase();
    const termCnpj = term.replace(/[.\-/\s]/g, '').toUpperCase();
    const filtered = (data?.data ?? []).filter(company => {
      if (!term) return true;
      return (
        company.legalName.toLowerCase().includes(term) ||
        (termCnpj ? company.cnpj.toUpperCase().includes(termCnpj) : false)
      );
    });

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'legalName':
          comparison = a.legalName.localeCompare(b.legalName, 'pt-BR');
          break;
        case 'cnpj':
          comparison = a.cnpj.localeCompare(b.cnpj);
          break;
        case 'trial':
          comparison = (a.trialEndsAt ?? '').localeCompare(b.trialEndsAt ?? '');
          break;
        case 'customerPercentage':
          comparison = a.customerPercentage - b.customerPercentage;
          break;
        case 'aiPercentage':
          comparison = a.aiPercentage - b.aiPercentage;
          break;
        case 'storagePercentage':
          comparison = a.storagePercentage - b.storagePercentage;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data?.data, search, sortBy, sortOrder]);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Trials
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Empresas trial ativas, expiradas e com contato comercial solicitado.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CleaningServicesIcon />}
          onClick={() => setCleanupDialogOpen(true)}
        >
          Limpar trials expirados
        </Button>
      </Stack>

      <TextField
        fullWidth
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: 'background.paper' },
        }}
        placeholder="Buscar por razão social ou CNPJ..."
        value={search}
        onChange={event => setSearch(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearch('')}
                  edge="end"
                  aria-label="Limpar busca"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error, 'Erro ao carregar trials')}
        </Alert>
      )}

      <Paper sx={{ position: 'relative', overflow: 'hidden' }}>
        {isFetching && !isLoading && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
        )}
        {isLoading ? (
          <CompanyTableSkeleton rows={Math.min(limit, 8)} columns={9} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableCell
                    label="Empresa"
                    sortKey="legalName"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <TableCell>Status</TableCell>
                  <SortableTableCell
                    label="Trial"
                    sortKey="trial"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <TableCell>Contato</TableCell>
                  <TableCell>Dados do cadastro</TableCell>
                  <TableCell>Upload trial</TableCell>
                  <SortableTableCell
                    label="Clientes"
                    sortKey="customerPercentage"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableCell
                    label="IA"
                    sortKey="aiPercentage"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableCell
                    label="Storage"
                    sortKey="storagePercentage"
                    currentSortBy={sortBy}
                    currentSortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSorted.map(company => {
                  const status = trialStatus(company);
                  return (
                    <TableRow
                      key={company.id}
                      hover
                      onClick={() => navigate(`/companies/${company.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {company.legalName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {formatCNPJ(company.cnpj)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          variant={
                            company.trialLifecycleStatus === 'cleaned' ? 'outlined' : 'filled'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {formatDate(company.trialStartedAt)} - {formatDate(company.trialEndsAt)}
                        </Typography>
                        {company.trialCleanedAt && (
                          <Typography variant="caption" color="text.secondary">
                            Limpo em {formatDate(company.trialCleanedAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {company.commercialContactRequestedAt ? (
                          <Stack spacing={0.25}>
                            <Chip label="Solicitado" color="info" size="small" />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(company.commercialContactRequestedAt)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Chip label="Não solicitado" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {formatPhone(company.adminPhone)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {company.managedCompanies
                            ? `${company.managedCompanies} empresas`
                            : 'Empresas não informadas'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <UsageCell
                          used={company.trialUploadsUsed}
                          limit={company.trialUploadLimit}
                          percentage={
                            company.trialUploadLimit
                              ? Math.round(
                                  (company.trialUploadsUsed / company.trialUploadLimit) * 100
                                )
                              : 0
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <UsageCell
                          used={company.customerCount}
                          limit={company.customerLimit}
                          percentage={company.customerPercentage}
                        />
                      </TableCell>
                      <TableCell>
                        <UsageCell
                          used={company.aiTokensUsed}
                          limit={company.aiTokenLimit}
                          percentage={company.aiPercentage}
                          unit="tokens"
                        />
                      </TableCell>
                      <TableCell>
                        <UsageCell
                          used={company.storageUsed}
                          limit={company.storageLimit}
                          percentage={company.storagePercentage}
                          formatter={formatBytes}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={event => event.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Ver detalhes">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/companies/${company.id}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Impersonar empresa">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => impersonateMutation.mutate(company.id)}
                                disabled={
                                  impersonateMutation.isPending &&
                                  impersonateMutation.variables === company.id
                                }
                              >
                                <LoginIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredAndSorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {search
                          ? 'Nenhum trial corresponde à busca nesta página'
                          : 'Nenhum trial encontrado'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {data && data.pagination.total > 0 && (
          <TablePagination
            component="div"
            count={data.pagination.total}
            page={page - 1}
            onPageChange={(_, nextPage) => setPage(nextPage + 1)}
            rowsPerPage={limit}
            onRowsPerPageChange={event => {
              setLimit(parseInt(event.target.value, 10));
              setPage(1);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        )}
      </Paper>

      <Dialog
        open={cleanupDialogOpen}
        onClose={() => !cleanupMutation.isPending && setCleanupDialogOpen(false)}
        aria-labelledby="cleanup-trials-dialog-title"
      >
        <DialogTitle id="cleanup-trials-dialog-title">Limpar trials expirados?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta ação remove permanentemente os dados operacionais de todos os trials expirados.
            As contas permanecem disponíveis para contratação.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCleanupDialogOpen(false)}
            disabled={cleanupMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => cleanupMutation.mutate()}
            disabled={cleanupMutation.isPending}
          >
            {cleanupMutation.isPending ? 'Limpando...' : 'Confirmar limpeza'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
